import { useQuery } from "@tanstack/react-query";
import {
  TEACHER_DASHBOARD_KEY,
  TEACHER_DASHBOARD_STALE_MS,
} from "@/features/first-run/dashboardQuery";
import {
  ONBOARDING_STATUS_KEY,
  ONBOARDING_STATUS_STALE_MS,
} from "@/features/onboarding/onboardingStatusQuery";
import { onboardingApi, teacherApi } from "@/lib/api";
import { deriveSetupGate } from "./firstRunModel";

// ONB-2 — the guided-setup gate hook. Reuses the EXACT readiness payloads the workspace / setup
// line already fetch (same query keys → deduped, no extra request, no desync), and derives the
// required-vs-usage step model. When `data` is supplied (a parent already holds the payload, e.g.
// the teacher workspace), we derive from it directly and skip our own fetch.
//
// role: "teacher" | "admin".
export function useSetupGate(role, { data } = {}) {
  const isTeacher = role === "teacher";
  const isAdmin = role === "admin";
  const roleValid = isTeacher || isAdmin; // super-admin / unknown roles never gate
  const hasOverride = data !== undefined;
  const query = useQuery({
    // teacher reuses ["teacher-dashboard", "semester"]; admin reuses ["onboarding-status"].
    queryKey: isTeacher ? TEACHER_DASHBOARD_KEY : ONBOARDING_STATUS_KEY,
    queryFn: () =>
      (isTeacher ? teacherApi.myDashboard({}) : onboardingApi.status()).then((res) => res.data),
    retry: false,
    enabled: roleValid && !hasOverride, // hook is always called; the fetch is what's gated
    // SPEED fix 7: the SAME stale window as the page that displays these numbers — see
    // features/first-run/dashboardQuery.js.
    staleTime: isTeacher ? TEACHER_DASHBOARD_STALE_MS : ONBOARDING_STATUS_STALE_MS,
  });

  // A role with no setup gate (super-admin, unknown) resolves as complete with nothing to show, and
  // never fetches — safe to call this hook unconditionally (e.g. from AuthPage before role is known).
  if (!roleValid) {
    return {
      role, steps: [], requiredSteps: [], usageSteps: [],
      requiredComplete: true, requiredDoneCount: 0, requiredTotal: 0,
      setupHref: null, loading: false, error: false, show: false,
    };
  }

  const payload = hasOverride ? data || {} : query.data || {};
  const loading = hasOverride ? false : query.isLoading;
  const error = hasOverride ? false : query.isError;
  const gate = deriveSetupGate(role, payload);

  return {
    ...gate,
    loading,
    error,
    // The banner/header render only once the data has loaded cleanly AND a required step is open.
    // A clean-but-complete account (or a load error) shows nothing — never a false gate.
    show: !loading && !error && !gate.requiredComplete,
  };
}
